"use client";

import { useMemo, useState } from "react";
import { apiFetch } from "@/lib/client-api";
import {
  styleSkillInput,
  type MediaStyleEditable,
  type MediaStyleSystem,
} from "@/lib/media-style";
import type { MediaOutputType } from "@/lib/media";

type StyleBusy = "save" | "activate" | "inspiration" | null;

type InspirationForm = {
  sourceId: string;
  name: string;
  platform: "Instagram" | "X" | "Threads" | "YouTube" | "TikTok" | "Twitch" | "Website" | "Other";
  url: string;
  traits: string;
  note: string;
};

const emptyInspiration: InspirationForm = {
  sourceId: "",
  name: "",
  platform: "Instagram",
  url: "",
  traits: "",
  note: "Use only high-level traits. Keep the resulting Asymmetric work original.",
};

function lines(value: string) {
  return value.split("\n").map((item) => item.trim()).filter(Boolean);
}

async function styleRequest(body: unknown) {
  const response = await apiFetch("/api/media/styles", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json() as MediaStyleSystem & { error?: string };
  if (!response.ok) throw new Error(payload.error || "The style change could not be saved.");
  return payload;
}

export default function MediaStyleEditor({
  system,
  mediaType,
  disabled,
  onSystemChange,
  onError,
  onOpenSource,
  initiallyOpen = false,
}: {
  initiallyOpen?: boolean;
  system: MediaStyleSystem;
  mediaType: MediaOutputType;
  disabled: boolean;
  onSystemChange: (system: MediaStyleSystem) => void;
  onError: (message: string | null) => void;
  onOpenSource?: (sourceId: string) => void;
}) {
  const activeSkill = useMemo(() => {
    const activeId = system.activeSkillByMediaType[mediaType];
    return system.skills.find((skill) => skill.id === activeId && skill.mediaType === mediaType)
      ?? system.skills.find((skill) => skill.mediaType === mediaType)
      ?? null;
  }, [mediaType, system]);
  const versions = useMemo(() => system.skills
    .filter((skill) => skill.mediaType === mediaType)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [mediaType, system.skills]);
  const inspirations = useMemo(() => system.inspirations
    .filter((inspiration) => inspiration.mediaTypes.includes(mediaType)), [mediaType, system.inspirations]);
  const [editable, setEditable] = useState<MediaStyleEditable | null>(
    activeSkill ? styleSkillInput(activeSkill) : null,
  );
  const [busy, setBusy] = useState<StyleBusy>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inspiration, setInspiration] = useState<InspirationForm>(emptyInspiration);

  if (!activeSkill || !editable) return null;
  const selectedSkill = activeSkill;

  function update<Key extends keyof MediaStyleEditable>(key: Key, value: MediaStyleEditable[Key]) {
    setEditable((current) => current ? { ...current, [key]: value } : current);
  }

  async function save(mode: "version" | "branch") {
    setBusy("save");
    setNotice(null);
    onError(null);
    try {
      const next = await styleRequest({
        action: "save_skill",
        mediaType,
        familyId: selectedSkill.familyId,
        mode,
        editable,
      });
      onSystemChange(next);
      setNotice(mode === "branch" ? "New style skill created and activated." : "New style version saved and activated.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "The style change could not be saved.");
    } finally {
      setBusy(null);
    }
  }

  async function activate(skillId: string) {
    if (skillId === selectedSkill.id) return;
    setBusy("activate");
    setNotice(null);
    onError(null);
    try {
      const next = await styleRequest({ action: "activate_skill", mediaType, skillId });
      onSystemChange(next);
      setNotice("Active style changed.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "The style could not be activated.");
    } finally {
      setBusy(null);
    }
  }

  async function addInspiration(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("inspiration");
    setNotice(null);
    onError(null);
    try {
      const next = await styleRequest({
        action: "add_inspiration",
        sourceId: inspiration.sourceId.trim(),
        name: inspiration.name.trim(),
        platform: inspiration.platform,
        url: inspiration.url.trim(),
        mediaTypes: [mediaType],
        traits: lines(inspiration.traits),
        note: inspiration.note.trim(),
      });
      onSystemChange(next);
      setInspiration(emptyInspiration);
      setNotice("Inspiration source added to this media type.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "The inspiration source could not be added.");
    } finally {
      setBusy(null);
    }
  }

  async function removeInspiration(inspirationId: string, name: string) {
    if (!window.confirm(`Remove ${name} from this curated inspiration registry?`)) return;
    setBusy("inspiration");
    setNotice(null);
    onError(null);
    try {
      const next = await styleRequest({ action: "remove_inspiration", inspirationId });
      onSystemChange(next);
      setNotice("Inspiration source removed.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "The inspiration source could not be removed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <details className="media-style-editor" open={initiallyOpen || undefined}>
      <summary>
        <div><span>Style skill</span><strong>{selectedSkill.name}</strong></div>
        <small>v{selectedSkill.version} · {inspirations.length} {inspirations.length === 1 ? "inspiration" : "inspirations"}</small>
      </summary>
      <div className="media-style-body">
        <div className="media-style-toolbar">
          <label>
            Active version
            <select
              value={selectedSkill.id}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => void activate(event.target.value)}
            >
              {versions.map((skill) => (
                <option value={skill.id} key={skill.id}>{skill.name} · v{skill.version}</option>
              ))}
            </select>
          </label>
          <span>{selectedSkill.builtIn ? "Built-in base" : "Custom revision"} · older versions remain available</span>
        </div>

        <div className="media-style-fields">
          <label>
            Skill name
            <input
              value={editable.name}
              maxLength={80}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("name", event.target.value)}
            />
          </label>
          <label className="wide">
            Creative brief
            <textarea
              value={editable.creativeBrief}
              maxLength={1_500}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("creativeBrief", event.target.value)}
            />
          </label>
          <label>
            Voice rules · one per line
            <textarea
              value={editable.voiceRules.join("\n")}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("voiceRules", lines(event.target.value))}
            />
          </label>
          <label>
            Visual rules · one per line
            <textarea
              value={editable.visualRules.join("\n")}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("visualRules", lines(event.target.value))}
            />
          </label>
          <label>
            Humor rules · one per line
            <textarea
              value={editable.humorRules.join("\n")}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("humorRules", lines(event.target.value))}
            />
          </label>
          <label>
            Avoid · one per line
            <textarea
              value={editable.avoidRules.join("\n")}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("avoidRules", lines(event.target.value))}
            />
          </label>
          <label>
            Visual mode
            <select
              value={editable.imageMode}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("imageMode", event.target.value as MediaStyleEditable["imageMode"])}
            >
              <option value="typographic">Typographic / directed</option>
              <option value="illustrated">Original generated art + verified overlays</option>
            </select>
          </label>
          <label className="wide">
            Image art direction
            <textarea
              value={editable.imagePrompt}
              maxLength={1_000}
              disabled={disabled || Boolean(busy)}
              onChange={(event) => update("imagePrompt", event.target.value)}
            />
          </label>
        </div>

        <details className="media-style-advanced">
          <summary>Palette</summary>
          <div>
            {(Object.keys(editable.palette) as Array<keyof MediaStyleEditable["palette"]>).map((key) => (
              <label key={key}>
                {key}
                <span><input
                  type="color"
                  value={editable.palette[key]}
                  disabled={disabled || Boolean(busy)}
                  onChange={(event) => update("palette", { ...editable.palette, [key]: event.target.value })}
                />{editable.palette[key]}</span>
              </label>
            ))}
          </div>
        </details>

        <footer className="media-style-actions">
          <span>{notice ?? "Saving creates an auditable revision; it does not publish content."}</span>
          <div>
            <button type="button" disabled={disabled || Boolean(busy)} onClick={() => void save("branch")}>Save as new skill</button>
            <button type="button" disabled={disabled || Boolean(busy)} onClick={() => void save("version")}>
              {busy === "save" ? "Saving…" : "Save new version"}
            </button>
          </div>
        </footer>

        <section className="media-inspiration-register">
          <header><span>Curated inspiration</span><strong>{inspirations.length}</strong></header>
          {inspirations.length ? inspirations.map((record) => (
            <article key={record.id}>
              <div><strong>{record.name}</strong><span>{record.platform} · Source {record.sourceId}</span></div>
              <p>{record.traits.join(" · ")}</p>
              <small>{record.note}</small>
              <footer>
                <a href={record.url} target="_blank" rel="noreferrer">Open reference</a>
                {onOpenSource && <button type="button" onClick={() => onOpenSource(record.sourceId)}>View source</button>}
                <button type="button" disabled={disabled || Boolean(busy)} onClick={() => void removeInspiration(record.id, record.name)}>Remove</button>
              </footer>
            </article>
          )) : <p className="media-style-empty">No inspiration sources are scoped to this media type.</p>}
        </section>

        <details className="media-add-inspiration">
          <summary>Add inspiration source</summary>
          <form onSubmit={addInspiration}>
            <label>Source ID<input required value={inspiration.sourceId} onChange={(event) => setInspiration((current) => ({ ...current, sourceId: event.target.value }))} /></label>
            <label>Name<input required value={inspiration.name} onChange={(event) => setInspiration((current) => ({ ...current, name: event.target.value }))} /></label>
            <label>Platform<select value={inspiration.platform} onChange={(event) => setInspiration((current) => ({ ...current, platform: event.target.value as InspirationForm["platform"] }))}>
              {(["Instagram", "X", "Threads", "YouTube", "TikTok", "Twitch", "Website", "Other"] as const).map((platform) => <option key={platform}>{platform}</option>)}
            </select></label>
            <label>Reference URL<input required type="url" value={inspiration.url} onChange={(event) => setInspiration((current) => ({ ...current, url: event.target.value }))} /></label>
            <label className="wide">Traits · one per line<textarea required value={inspiration.traits} onChange={(event) => setInspiration((current) => ({ ...current, traits: event.target.value }))} /></label>
            <label className="wide">Curation note<textarea required value={inspiration.note} onChange={(event) => setInspiration((current) => ({ ...current, note: event.target.value }))} /></label>
            <footer><span>Scoped to this {mediaType} skill set.</span><button disabled={disabled || Boolean(busy)} type="submit">{busy === "inspiration" ? "Saving…" : "Add source"}</button></footer>
          </form>
        </details>
      </div>
    </details>
  );
}
